import React, { useState, useEffect, useCallback } from "react";
import Confetti from "react-confetti";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Geolocation } from "@capacitor/geolocation";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter} from "@/components/ui/dialog";
import { User, Calendar, FileText, CreditCard, Clock, FolderOpen, TrendingUp, Activity, Gift, PartyPopper, Cake, AlertCircle, Star, AlertTriangle, Info, UserCheck, UserX, Loader2, Sparkles} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fetchEvents, getEmployees, fetchHolidays, fetchCompanyLeaveConfiguration} from "@/services/adminService";
import { getMe } from "@/services/authService";
import { getEmployeeLeaveDashboard } from "@/services/leaveService";
import { getMyMonthlyAttendanceSummary, getRatingData} from "@/services/employeeService";
import { getEmployeeDashboardCalendarData, createOTLog} from "@/services/attendanceService";
import { recordAttendance } from "@/services/employeeService";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Device } from "@capacitor/device";
import { Preferences } from "@capacitor/preferences";
import { Skeleton } from "@/components/ui/skeleton";
import { getErrorMessage } from "@/utils/errorHandler";
import PerformanceRatingDrawer from "@/components/employee/PerformanceRatingDrawer";
import RatingMeter from "@/components/ui/RatingMeter";
import {
  clampRating,
  ratingBand,
  resolveScale,
} from "@/lib/ratingScale";

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [leaveEntitlement, setLeaveEntitlement] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employeeData, setEmployeeData] = useState();
  const [attendanceData, setAttendanceData] = useState({
    todayStatus: "checked-out",
    checkInTime: "",
    checkOutTime: "",
    totalHours: "0h 0m",
    thisMonthHours: "0h",
    regularHours: 0,
    otHours: 0,
    isCheckinOutEnabled: false,
    todaysWorkingDuration: 0,
    requires_daily_login: false,
  });
  const [todayConfig, setTodayConfig] = useState();
  const [countdown, setCountdown] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [notifications, setNotifications] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [isBirthday, setIsBirthday] = useState(false);
  const [rating, setRating] = useState(null);
  const [ratingLoading, setRatingLoading] = useState(true);
  const [ratingError, setRatingError] = useState("");
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [isOnProbation, setIsOnProbation] = useState(false);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEarlyCheckoutDialog, setShowEarlyCheckoutDialog] = useState(false);
  const [earlyCheckoutData, setEarlyCheckoutData] = useState(null);
  const [showOTDialog, setShowOTDialog] = useState(false);
  const [openOTDialog, setOpenOTDialog] = useState(false);
  const [otContext, setOTContext] = useState(null);
  const { hasFeature } = useSubscription();
  const hasPerformanceRatings = hasFeature("performance_ratings");
  // Admin-configured scale (rating_config base_rating + max_rating), served by
  // getRatingData. Only the scale is configurable — the rating value itself is
  // whatever the existing formula produced. Bands are anchored to the base, so
  // both halves are needed and must come from the same payload.
  const ratingScale = resolveScale(rating);
  const maxRating = ratingScale.max;
  // The value comes from employeeData but the scale comes from `rating`, a
  // separate request. Until it lands, the band would be computed against the
  // fallback scale and could flip once the real one arrives — so the label is
  // held back while the numeric value and bar render immediately.
  const ratingScaleReady = !ratingLoading && Boolean(rating);
  const canApplyLoan = hasFeature("loan_management");
  const canUploadDocuments = hasFeature("document_upload");
  const { addNotificationListener } = useWebSocket();
  const [otForm, setOTForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    from_time: "",
    to_time: "",
    project: "",
    notes: "",
  });
  const [showDailyConfetti, setShowDailyConfetti] = useState(false);
  const [confettiWindow, setConfettiWindow] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });
 
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [total, setTotal] = useState(0);
  const [present_rate, setPresentRate] = useState(0);
  const [on_time_rate, setOnTimeRate] = useState(0);
  const [items, setItems] = useState([]);
  const [otHours, setOtHours] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loc, setLoc] = useState(null);
  const [device, setDevice] = useState(null);

  useEffect(() => {
    getMyself();
    logDeviceInfo();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateWindowSize = () => {
      setConfettiWindow({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);
    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  useEffect(() => {
    const employeeId = employeeData?.employee?.employee_id;
    const dateOfBirth = employeeData?.employee?.date_of_birth;
    if (!employeeId) return;

    const today = new Date();
    const parseDobLocal = (dobValue) => {
      if (!dobValue) return null;
      const datePart = String(dobValue).split("T")[0];
      const [year, month, day] = datePart.split("-").map(Number);
      if (!year || !month || !day) return null;
      return new Date(year, month - 1, day);
    };

    const dob = parseDobLocal(dateOfBirth);
    const isUserBirthdayToday =
      dob &&
      today.getMonth() === dob.getMonth() &&
      today.getDate() === dob.getDate();

    if (!isUserBirthdayToday) {
      setShowDailyConfetti(false);
      return;
    }

    const todayKey = format(today, "yyyy-MM-dd");
    const confettiStorageKey = `employee-dashboard-confetti-${employeeId}`;
    let isCancelled = false;
    let timeout = null;

    const runConfettiCheck = async () => {
      try {
        const { value: lastShownDate } = await Preferences.get({
          key: confettiStorageKey,
        });
        if (isCancelled || lastShownDate === todayKey) return;

        await Preferences.set({ key: confettiStorageKey, value: todayKey });
        if (isCancelled) return;

        setShowDailyConfetti(true);
        timeout = setTimeout(() => {
          if (!isCancelled) setShowDailyConfetti(false);
        }, 4500);
      } catch (error) {
        if (isCancelled) return;
        setShowDailyConfetti(true);
        timeout = setTimeout(() => {
          if (!isCancelled) setShowDailyConfetti(false);
        }, 4500);
      }
    };

    runConfettiCheck();

    return () => {
      isCancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [employeeData?.employee?.employee_id, employeeData?.employee?.date_of_birth]);

  const getMyself = async () => {
    try {
      const res = await getMe();
      setEmployeeData(res);
    } catch (error) {}
  };

  useEffect(() => {
    if (employeeData?.employee.employee_id) {
      fetchMonthlySummary();
      fetchMonthlyAttendance();
      fetchUpcomingEvents();
      checkBirthday();
      loadDashboard();
      fetchEmployeeRating();
    }
  }, [employeeData]);

  const logDeviceInfo = async () => {
    const info = await Device.getInfo();
    setDevice(info);
  };

  const loadDashboard = async () => {
    try {
      const res = await getEmployeeLeaveDashboard();
      setLeaveEntitlement(res.dashboard_leave_balance || []);
      setIsOnProbation(res.employee.is_on_probation);
      setLeaveTypes(res.dashboard_leave_types || []);
    } catch (err) {
      toast.error("Failed to load dashboard");
    }
  };

  useEffect(() => {
    if (
      attendanceData.todayStatus === "before-work" ||
      attendanceData.todayStatus === "after-work"
    ) {
      const interval = setInterval(() => {
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const workStartTime = 8 * 60 + 30;
        const workEndTime = 17 * 60 + 30;
        let targetTime;
        if (attendanceData.todayStatus === "before-work") {
          targetTime = workStartTime;
        } else {
          targetTime = workStartTime + 24 * 60;
        }
        let diffMinutes = targetTime - currentMinutes;
        if (diffMinutes < 0) diffMinutes += 24 * 60;
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        const seconds = 60 - now.getSeconds();
        setCountdown({ hours, minutes, seconds });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [attendanceData.todayStatus]);

  const checkBirthday = () => {
    if (!employeeData?.employee.date_of_birth) return;
    const today = new Date();
    const dob = new Date(employeeData?.employee.date_of_birth);
    const isBday =
      today.getMonth() === dob.getMonth() && today.getDate() === dob.getDate();
    setIsBirthday(isBday);
  };

  const fetchUpcomingEvents = async () => {
    try {
      const events = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let allEmployees = [];
      try {
        const employeesResponse = await getEmployees();
        allEmployees = employeesResponse;
        const activeEmployees = allEmployees.filter(
          (emp) => emp.status === "active"
        );

        activeEmployees.forEach((emp) => {
          if (emp.date_of_birth) {
            const dob = new Date(emp.date_of_birth);
            const nextBirthday = new Date(
              today.getFullYear(),
              dob.getMonth(),
              dob.getDate()
            );
            nextBirthday.setHours(0, 0, 0, 0);
            if (nextBirthday < today) {
              nextBirthday.setFullYear(today.getFullYear() + 1);
            }
            const daysUntilBirthday = Math.floor(
              (nextBirthday - today) / (1000 * 60 * 60 * 24)
            );
            if (daysUntilBirthday <= 30 && daysUntilBirthday >= 0) {
              events.push({
                type: "birthday",
                title: `${emp.name}'s Birthday`,
                subtitle: emp._id === employeeData.employee._id ? "(You!)" : "",
                date: nextBirthday,
                daysUntil: daysUntilBirthday,
                icon: "🎂",
              });
            }
          }

          if (emp.date_of_joining) {
            const joiningDate = new Date(emp.date_of_joining);
            const nextAnniversary = new Date(
              today.getFullYear(),
              joiningDate.getMonth(),
              joiningDate.getDate()
            );
            nextAnniversary.setHours(0, 0, 0, 0);
            if (nextAnniversary < today) {
              nextAnniversary.setFullYear(today.getFullYear() + 1);
            }
            // Years completed *at* the upcoming anniversary date, not as of
            // today -- the anniversary may have rolled into next year.
            const yearsWorked =
              nextAnniversary.getFullYear() - joiningDate.getFullYear();
            const daysUntilAnniversary = Math.floor(
              (nextAnniversary - today) / (1000 * 60 * 60 * 24)
            );
            // yearsWorked < 1 means this date is the joining date itself
            // (today, or a future start), not a work anniversary. The raw
            // joining date is not an upcoming event, so skip it.
            if (
              daysUntilAnniversary <= 30 &&
              daysUntilAnniversary >= 0 &&
              yearsWorked >= 1
            ) {
              events.push({
                type: "anniversary",
                title: `${emp.name}${
                  emp.employee_id === employeeData.employee.employee_id
                    ? " (You)"
                    : ""
                } - ${yearsWorked} Year${yearsWorked > 1 ? "s" : ""}`,
                date: nextAnniversary,
                daysUntil: daysUntilAnniversary,
                icon: "🎉",
              });
            }
          }
        });
      } catch (error) {
        console.error("Error fetching employees for events:", error);
      }

      try {
        const holidaysResponse = await fetchHolidays();
        const holidays = holidaysResponse || [];
        const upcomingHolidays = holidays
          .map((h) => ({ ...h, date: new Date(h.date) }))
          .filter((h) => {
            h.date.setHours(0, 0, 0, 0);
            const daysUntil = Math.floor(
              (h.date - today) / (1000 * 60 * 60 * 24)
            );
            return daysUntil >= 0 && daysUntil <= 30;
          })
          .sort((a, b) => a.date - b.date)
          .map((h) => ({
            type: "holiday",
            title: h.name,
            date: h.date,
            daysUntil: Math.floor((h.date - today) / (1000 * 60 * 60 * 24)),
            icon: "🎊",
          }));
        events.push(...upcomingHolidays);
      } catch (error) {
        console.error("Error fetching holidays:", error);
      }

      try {
        const adminEventsResponse = await fetchEvents();
        const adminEvents = adminEventsResponse.data.events || [];
        const processedAdminEvents = adminEvents
          .map((event) => {
            const eventDate = new Date(event.date);
            eventDate.setHours(0, 0, 0, 0);
            const daysUntil = Math.floor(
              (eventDate - today) / (1000 * 60 * 60 * 24)
            );
            const eventIcons = {
              company: "🏢",
              team: "👥",
              milestone: "🎯",
              other: "📅",
            };
            return {
              type: "event",
              title: event.title,
              subtitle: event.description || "",
              date: eventDate,
              daysUntil: daysUntil,
              icon: eventIcons[event.event_type] || "📅",
            };
          })
          .filter((event) => event.daysUntil >= 0 && event.daysUntil <= 30);
        events.push(...processedAdminEvents);
      } catch (error) {
        console.error("Error fetching admin events:", error);
      }

      try {
        const leavesResponse = await fetchCompanyLeaveConfiguration();
        const allLeaves = leavesResponse.data.leave_config || [];
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        const upcomingLeaves = allLeaves
          .filter((leave) => leave.status === "approved")
          .map((leave) => ({
            ...leave,
            start_date: new Date(leave.start_date),
            end_date: new Date(leave.end_date),
          }))
          .filter((leave) => {
            leave.start_date.setHours(0, 0, 0, 0);
            leave.end_date.setHours(0, 0, 0, 0);
            return leave.start_date <= nextWeek && leave.end_date >= today;
          });

        const leavesByEmployee = {};
        upcomingLeaves.forEach((leave) => {
          if (!leavesByEmployee[leave.employee_id]) {
            const employee = allEmployees.find(
              (e) => e.employee_id === leave.employee_id
            );
            const employeeName = employee ? employee.name : "Unknown Employee";
            leavesByEmployee[leave.employee_id] = {
              employee_id: leave.employee_id,
              employee_name: employeeName,
              leaves: [],
            };
          }
          leavesByEmployee[leave.employee_id].leaves.push(leave);
        });

        Object.values(leavesByEmployee).forEach((empLeave) => {
          const earliestLeave = empLeave.leaves.sort(
            (a, b) => a.start_date - b.start_date
          )[0];
          const daysUntil = Math.floor(
            (earliestLeave.start_date - today) / (1000 * 60 * 60 * 24)
          );
          const isOnLeaveToday =
            earliestLeave.start_date <= today &&
            earliestLeave.end_date >= today;
          events.push({
            type: "leave",
            title: `${empLeave.employee_name}${
              empLeave.employee_id === employeeData.employee.employee_id
                ? " (You!)"
                : ""
            }`,
            subtitle: isOnLeaveToday
              ? "On leave today"
              : `Leave starts ${format(earliestLeave.start_date, "MMM dd")}`,
            date: earliestLeave.start_date,
            daysUntil: daysUntil,
            icon: "🏖️",
          });
        });
      } catch (error) {
        console.error("Error fetching leaves:", error);
      }

      events.sort((a, b) => {
        if (a.daysUntil === b.daysUntil) {
          if (a.type === "leave" && b.type !== "leave") return -1;
          if (b.type === "leave" && a.type !== "leave") return 1;
          return 0;
        }
        return a.daysUntil - b.daysUntil;
      });

      setUpcomingEvents(events.slice(0, 6));
    } catch (error) {
      console.error("Error fetching upcoming events:", error);
    }
  };

  const fetchMonthlySummary = async () => {
    try {
      const now = new Date();
      const month = now.getMonth() + 1;
      const year = now.getFullYear();
      const attendanceResponse = await getEmployeeDashboardCalendarData(
        year,
        month
      );
      const todayAttendanceRecord =
        attendanceResponse.data.todayAttendanceRecord;
      setTodayConfig(todayAttendanceRecord);

      const todayStatus = attendanceResponse.data.todayStatus;
      const CheckInTime = attendanceResponse.data.CheckInTime;
      const CheckOutTime = attendanceResponse.data.CheckOutTime;
      let todaysWorkingDuration = 0;

      if (attendanceResponse.data.isCheckinOutEnabled && attendanceResponse.data.requires_daily_login) {
        if (CheckOutTime) {
          todaysWorkingDuration =
            attendanceResponse.data.todayAttendanceRecord
              .work_duration_minutes / 60;
        }
      } else {
        todaysWorkingDuration =
          attendanceResponse.data.todayAttendanceRecord.work_duration_minutes /
          60;
      }

      // Calculate hours exactly like Attendance page
      let totalWorkingHoursMinutes =
        attendanceResponse.data.totalWorkDurationMinutes;
      const totalOTHours = Number(attendanceResponse?.data?.otHours ?? 0);
      setIsClockedIn(
        (attendanceResponse.data.isCheckinOutEnabled && attendanceResponse.data.requires_daily_login) && CheckInTime != null
          ? true
          : false
      );

      const totalRegularHours = totalWorkingHoursMinutes / 60;
      const totalHours = totalRegularHours + totalOTHours;

      setAttendanceData((prev) => ({
        ...prev,
        todayStatus,
        isOnApprovedLeave: Boolean(attendanceResponse.data.isOnApprovedLeaveToday),
        checkInTime: CheckInTime,
        checkOutTime: CheckOutTime,
        totalHours: String(totalHours),
        thisMonthHours: `${Math.round(totalHours)}h`,
        regularHours: totalRegularHours,
        otHours: totalOTHours,
        isCheckinOutEnabled: attendanceResponse.data.isCheckinOutEnabled,
        todaysWorkingDuration: todaysWorkingDuration,
        requires_daily_login: attendanceResponse.data.requires_daily_login,
      }));
    } catch (error) {
      console.error("Error fetching monthly summary:", error);
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case "payslips":
        navigate("/employee/payslips");
        break;
      case "leave":
        navigate("/employee/leave");
        break;
      case "loans":
        navigate("/employee/finances");
        break;
      case "profile":
        navigate("/employee/profile");
        break;
      case "attendance":
        navigate("/employee/attendance");
        break;
      case "documents":
        navigate("/employee/documents");
        break;
      default:
        break;
    }
  };

  const handleOTSubmit = async (e) => {
    e.preventDefault();
    const today = format(new Date(), "yyyy-MM-dd");
    if (otForm.date !== today) {
      toast.error("OT can only be logged for today's date");
      return;
    }

    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const [toHourCheck, toMinCheck] = otForm.to_time.split(":").map(Number);
    if (otForm.date === today) {
      const endTimeInMinutes = toHourCheck * 60 + toMinCheck;
      const currentTimeInMinutes = currentHour * 60 + currentMinute;
      if (endTimeInMinutes > currentTimeInMinutes) {
        toast.error(
          `You cannot log OT for future times. Current time is ${currentHour
            .toString()
            .padStart(2, "0")}:${currentMinute.toString().padStart(2, "0")}.`
        );
        return;
      }
    }

    const fromMinutes = otForm.from_time.split(":")[1];
    const toMinutes = otForm.to_time.split(":")[1];
    if (fromMinutes !== "00" && fromMinutes !== "30") {
      toast.error("From time minutes must be either 00 or 30");
      return;
    }
    if (toMinutes !== "00" && toMinutes !== "30") {
      toast.error("To time minutes must be either 00 or 30");
      return;
    }

    try {
      setLoading(true);
      await createOTLog(otForm);
      toast.success("OT hours logged successfully!");
      setShowOTDialog(false);
      setOTForm({
        date: format(new Date(), "yyyy-MM-dd"),
        from_time: todayConfig?.shift?.end_time,
        to_time: "",
        project: "",
        notes: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log OT hours");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const getCurrentPosition = useCallback(async () => {
    console.log("[GPS] Requesting location…");

    try {
      //
      // STEP 1 — Request permission
      //
      const permResult = await Geolocation.requestPermissions();

      if (permResult.location !== "granted") {
        console.warn("[GPS] Permission denied");
        alert("Location permission is required. Please allow it.");
        return null;
      }

      console.log("[GPS] Permission granted");

      //
      // STEP 2 — Try multiple readings, keep best
      //
      const MAX_ATTEMPTS = 5;
      const TARGET_ACCURACY_M = 80;
      const DELAY_MS = 1500;

      let bestPosition = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        console.log(`[GPS] Attempt ${attempt}/${MAX_ATTEMPTS}`);

        const pos = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });

        const accuracy =
          pos && pos.coords && typeof pos.coords.accuracy === "number"
            ? pos.coords.accuracy
            : Number.MAX_SAFE_INTEGER;

        console.log(
          `[GPS] Reading → lat=${pos.coords.latitude}, lon=${pos.coords.longitude}, accuracy=${accuracy}m`
        );

        if (
          !bestPosition ||
          accuracy < (bestPosition.coords.accuracy || Number.MAX_SAFE_INTEGER)
        ) {
          bestPosition = pos;
        }

        // ✅ Acceptable accuracy achieved
        if (accuracy <= TARGET_ACCURACY_M) {
          console.log("[GPS] Acceptable accuracy achieved");
          setLoc(pos);
          return pos;
        }

        // wait before next attempt
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, DELAY_MS));
        }
      }

      //
      // STEP 3 — Fallback to best available reading
      //
      if (bestPosition) {
        const bestAccuracy = Math.round(bestPosition.coords.accuracy || 0);

        console.warn(`[GPS] Using best available accuracy: ${bestAccuracy}m`);

        setLoc(bestPosition);

        alert(
          `⚠ GPS accuracy is poor (${bestAccuracy}m).\nMove outdoors or near a window and try again.`
        );

        return bestPosition;
      }

      throw new Error("No GPS readings obtained");
    } catch (err) {
      console.error("[GPS] Failed to fetch location", err);
      alert("Unable to fetch location. Please enable GPS and try again.");
      return null;
    }
  }, []);

  const verifyPunchIdentity = async () => {
    console.log("in the function");
    if (!Capacitor.isNativePlatform()) return true;

    try {
      await NativeBiometric.verifyIdentity({
        reason: "Confirm your identity to record attendance",
        title: "Zenith Attendance",
        subtitle: "Use fingerprint or device PIN",
        fallbackTitle: "Use device PIN",
      });
      return true;
    } catch {
      return false;
    }
  };

  const handlePunch = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    let currentLocation = null;

    try {
      const action = isClockedIn ? "punch_out" : "punch_in";
      const now = new Date();

      // ------------------
      // PUNCH IN
      // ------------------
      if (action === "punch_in") {
        if (device.platform !== "web") {
          currentLocation = await getCurrentPosition();
          if (!currentLocation) throw new Error("LOCATION_REQUIRED");

          const verified = await verifyPunchIdentity();
          if (!verified) {
            toast.error("Authentication required to punch");
            return;
          }
        }

        const payload = {
          action,
          device,
          location: currentLocation,
          time: now.toISOString(),
          timezoneOffset: now.getTimezoneOffset(),
          isPunchEmpolyeeInEarly: true,
        };

        const response = await recordAttendance(payload);
        if (!response?.success) {
          throw new Error(response?.message || "Punch in failed");
        }

        await fetchMonthlySummary();
        return;
      }

      // ------------------
      // PUNCH OUT
      // ------------------
      if (!todayConfig?.shift?.end_time) {
        throw new Error("SHIFT_NOT_CONFIGURED");
      }

      const [endHour, endMinute] = todayConfig.shift.end_time
        .split(":")
        .map(Number);
      const shiftEnd = new Date();
      shiftEnd.setHours(endHour, endMinute, 0, 0);

      if (now < shiftEnd) {
        setEarlyCheckoutData({
          shiftEndTime: todayConfig.shift.end_time,
          currentTime: now.toTimeString().slice(0, 5),
        });
        setShowEarlyCheckoutDialog(true);
        return;
      }

      if (device.platform !== "web") {
        currentLocation = await getCurrentPosition();
        if (!currentLocation) throw new Error("LOCATION_REQUIRED");
      }

      const payload = {
        action,
        device,
        location: currentLocation,
        time: now.toISOString(),
        timezoneOffset: now.getTimezoneOffset(),
      };

      const response = await recordAttendance(payload);

      if (!response?.success) {
        throw new Error(response?.message || "Punch out failed");
      }

      if (response?.isEligibleForOT) {
        toast.info("You are eligible for overtime");
      }

      await fetchMonthlySummary();
    } catch (error) {
      if (error.message === "LOCATION_REQUIRED") {
        toast.error("Enable location services and try again");
      } else if (error.message === "SHIFT_NOT_CONFIGURED") {
        toast.error("Shift end time not configured");
      } else {
        toast.error(
          error?.response?.data?.message ||
            error.message ||
            "Failed to record attendance"
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const fetchMonthlyAttendance = async () => {
    try {
      const response = await getMyMonthlyAttendanceSummary();
      const data = response?.data;
      if (!data) return;
      setTotal(data.totalTrackedDays || 0);
      setPresentRate(data.presentRate || 0);
      setOnTimeRate(data.onTimeRate || 0);
      setOtHours(data.otHours);
      setItems([
        {
          label: "Days Present",
          value: data.presentDays,
          icon: UserCheck,
          color: "bg-teal-500",
        },
        {
          label: "Days Absent",
          value: data.absentDays,
          icon: UserX,
          color: "bg-red-500",
        },
        {
          label: "Days Arrived Late",
          value: data.lateDays,
          icon: Calendar,
          color: "bg-yellow-500",
        },
        {
          label: "Total OT Hours",
          value: data.otHours,
          icon: Clock,
          color: "bg-indigo-500",
        },
      ]);
    } catch (err) {
      console.error("Failed to fetch monthly attendance", err);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const fetchEmployeeRating = async () => {
    setRatingLoading(true);
    setRatingError("");
    try {
      const response = await getRatingData();
      const data = response?.data;
      if (!data) return;
      setRating(data);
    } catch (error) {
      console.error("Error fetching employee rating:", error);
      // Surfaced inside the drawer, which previously rendered blank on failure.
      setRatingError(getErrorMessage(error, "Failed to load performance rating"));
    } finally {
      setRatingLoading(false);
    }
  };

  const handleEarlyCheckout = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    let currentLocation = null;

    try {
      // 📍 MOBILE ONLY — get GPS
      if (device.platform !== "web") {
        currentLocation = await getCurrentPosition();
        if (!currentLocation) {
          throw new Error("LOCATION_REQUIRED");
        }

        const verified = await verifyPunchIdentity();

        if (!verified) {
          toast.error("Authentication required to punch");
          return;
        }
      }

      const now = new Date();
      const payload = {
        action: "punch_out",
        device,
        location: currentLocation,
        time: now.toISOString(),
        timezoneOffset: now.getTimezoneOffset(),
      };

      const response = await recordAttendance(payload);

      if (!response?.success) {
        throw new Error(response?.message || "Punch out failed");
      }

      await fetchMonthlySummary();

      // ✅ CLOSE MODAL ONLY AFTER SUCCESS
      setShowEarlyCheckoutDialog(false);
      setEarlyCheckoutData(null);
    } catch (error) {
      if (error.message === "LOCATION_REQUIRED") {
        toast.error("Enable location services and try again");
      } else {
        toast.error(
          error?.response?.data?.message ||
            error.message ||
            "Failed to record attendance"
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Count visible quick actions to determine grid columns
  const quickActionsCount =
    4 + // payslips, leave, profile, attendance
    (canApplyLoan ? 1 : 0) +
    (canUploadDocuments ? 1 : 0);

  const quickActionsColsClass =
    quickActionsCount <= 2
      ? "grid-cols-2"
      : quickActionsCount <= 3
      ? "md:grid-cols-3"
      : quickActionsCount <= 4
      ? "md:grid-cols-4"
      : quickActionsCount <= 5
      ? "md:grid-cols-5"
      : "md:grid-cols-6";

  const leaveTotals = React.useMemo(() => {
    if (!leaveEntitlement || leaveEntitlement.length === 0) {
      return {
        total: 0,
        used: 0,
        available: 0,
        percentage: 0,
      };
    }

    const totals = leaveEntitlement.reduce(
      (acc, item) => {
        const allocated = item.total_allocated || 0; // already final
        const carried = item.carry_forwarded || 0;
        const used = item.used || 0;

        const total = allocated + carried;
        const available = Math.max(total - used, 0);

        return {
          total: acc.total + total,
          used: acc.used + used,
          available: acc.available + available,
        };
      },
      { total: 0, used: 0, available: 0 }
    );

    return {
      ...totals,
      percentage:
        totals.total > 0
          ? Math.min((totals.available / totals.total) * 100, 100)
          : 0,
    };
  }, [leaveEntitlement]);

  function isAfterShiftEnd(endTime) {
    if (!endTime) return false;

    const now = new Date();

    const [endHour, endMinute] = endTime.split(":").map(Number);

    const shiftEnd = new Date();
    shiftEnd.setHours(endHour, endMinute, 0, 0);

    return now > shiftEnd;
  }

  const firstName = employeeData?.user.name?.split(" ")[0] || "Champion";
  const birthDate = employeeData?.employee?.date_of_birth
    ? new Date(employeeData.employee.date_of_birth)
    : null;
  const birthdayYear =
    birthDate && !Number.isNaN(birthDate.getTime())
      ? new Date().getFullYear() - birthDate.getFullYear()
      : null;
  const birthdayAccentMessages = [
    "May this year bring bold wins and beautiful surprises.",
    "Today is all about joy, gratitude, and celebrating you.",
    "Your energy makes the team brighter every single day.",
  ];

  if (!employeeData) {
    return <EmployeeDashboardSkeleton />;
  }

  // An approved leave decides this even if today's attendance day still reads
  // "present" (a leave day whose record never picked the leave up). Standard unpaid
  // leave is stored as "absent", so only an absence without one is really absent.
  const isLeaveToday =
    attendanceData.isOnApprovedLeave ||
    attendanceData.todayStatus === "on-leave";
  const isAbsentToday =
    !isLeaveToday && attendanceData.todayStatus === "absent";

  return (
    <div className="space-y-8">
      {/* Birthday Banner */}
      {showDailyConfetti && (
        <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
          <Confetti
            width={confettiWindow.width}
            height={confettiWindow.height}
            numberOfPieces={showDailyConfetti ? 160 : 0}
            recycle={false}
            tweenDuration={4500}
            gravity={0.22}
            initialVelocityX={{ min: 8, max: 24 }}
            initialVelocityY={{ min: -30, max: -16 }}
            confettiSource={{
              x: 0,
              y: Math.max(confettiWindow.height - 28, 0),
              w: 36,
              h: 28,
            }}
            colors={["#f97316", "#ec4899", "#7c3aed", "#facc15"]}
            className="!left-0 !top-0"
          />
          <Confetti
            width={confettiWindow.width}
            height={confettiWindow.height}
            numberOfPieces={showDailyConfetti ? 160 : 0}
            recycle={false}
            tweenDuration={4500}
            gravity={0.22}
            initialVelocityX={{ min: -24, max: -8 }}
            initialVelocityY={{ min: -30, max: -16 }}
            confettiSource={{
              x: Math.max(confettiWindow.width - 36, 0),
              y: Math.max(confettiWindow.height - 28, 0),
              w: 36,
              h: 28,
            }}
            colors={["#f97316", "#ec4899", "#7c3aed", "#facc15"]}
            className="!left-0 !top-0"
          />
        </div>
      )}
      {isBirthday && (
        <Card className="relative overflow-hidden rounded-[1.75rem] border-0 bg-[linear-gradient(135deg,#f97316_0%,#ec4899_45%,#7c3aed_100%)] text-white shadow-[0_24px_60px_rgba(236,72,153,0.35)]">
          <div className="pointer-events-none absolute inset-0 opacity-30">
            <div className="absolute -left-12 top-0 h-40 w-40 animate-pulse rounded-full bg-white/35 blur-3xl" />
            <div className="absolute right-0 top-2 h-32 w-32 animate-pulse rounded-full bg-yellow-200/30 blur-3xl [animation-delay:300ms]" />
            <div className="absolute bottom-0 left-1/3 h-36 w-36 animate-pulse rounded-full bg-fuchsia-100/20 blur-3xl [animation-delay:700ms]" />
          </div>
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {[...Array(12)].map((_, index) => (
              <span
                key={`confetti-${index}`}
                className="absolute rounded-full bg-white/70 animate-bounce"
                style={{
                  width: `${8 + (index % 3) * 4}px`,
                  height: `${8 + (index % 3) * 4}px`,
                  left: `${6 + index * 7.5}%`,
                  top: `${10 + (index % 4) * 18}%`,
                  animationDelay: `${index * 120}ms`,
                  animationDuration: `${1.8 + (index % 3) * 0.4}s`,
                }}
              />
            ))}
          </div>
          <CardContent className="relative z-10 p-4 sm:p-6 md:p-8">
            <div className="flex flex-col gap-4 sm:gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 flex-col gap-3 sm:gap-4 text-center lg:text-left">
                <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                  <Badge
                    variant="outline"
                    className="border-white/20 bg-white/15 px-2.5 py-1 text-[0.58rem] uppercase tracking-[0.2em] text-white backdrop-blur-md sm:px-3 sm:text-[0.65rem] sm:tracking-[0.25em]"
                  >
                    Birthday Spotlight
                  </Badge>
                  {birthdayYear ? (
                    <Badge
                      variant="outline"
                      className="hidden border-0 bg-yellow-300/90 px-2.5 py-1 text-[0.68rem] font-semibold text-orange-950 shadow-lg shadow-yellow-950/10 sm:inline-flex sm:px-3 sm:text-[0.72rem]"
                    >
                      Level {birthdayYear} unlocked
                    </Badge>
                  ) : null}
                </div>
                <div className="space-y-2 sm:space-y-3">
                  <h2 className="text-[1.9rem] font-black leading-none tracking-tight sm:text-3xl md:text-4xl">
                    Happy Birthday, {firstName}!
                  </h2>
                  <p className="mx-auto max-w-xl text-[0.92rem] leading-5 text-white/90 sm:hidden">
                    Wishing you a joyful day filled with wins and good vibes.
                  </p>
                  <p className="mx-auto hidden max-w-2xl text-sm leading-6 text-white/90 sm:block md:text-base lg:mx-0">
                    {
                      birthdayAccentMessages[
                        new Date().getDate() % birthdayAccentMessages.length
                      ]
                    }{" "}
                    Wishing you joy, success, and unforgettable moments from
                    everyone at {employeeData?.companyName || "the team"}.
                  </p>
                </div>
                <div className="hidden flex-col items-stretch gap-2 sm:flex sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-3 lg:justify-start">
                  <div className="flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-md sm:justify-start">
                    <Gift className="h-4 w-4 animate-bounce text-yellow-200" />
                    <span className="text-sm font-medium text-white">
                      Your day, your moment
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 rounded-full border border-white/20 bg-black/15 px-4 py-2 backdrop-blur-md sm:justify-start">
                    <Sparkles className="h-4 w-4 animate-pulse text-pink-200" />
                    <span className="text-sm font-medium text-white">
                      Celebrating your journey with us
                    </span>
                  </div>
                </div>
              </div>
              <div className="mx-auto mt-1 flex w-full max-w-sm items-center justify-center lg:mx-0 lg:mt-0 lg:justify-end">
                <div className="relative flex h-28 w-28 items-center justify-center rounded-full border border-white/25 bg-white/10 shadow-2xl backdrop-blur-md sm:h-44 sm:w-44 md:h-48 md:w-48">
                  <div className="absolute inset-2.5 rounded-full border border-dashed border-white/35 animate-spin sm:inset-3 [animation-duration:16s]" />
                  <div className="absolute inset-6 rounded-full border border-white/20 sm:inset-8" />
                  <div className="absolute -top-2 left-4 hidden rounded-full bg-white/15 p-1.5 backdrop-blur-md animate-bounce sm:block sm:-top-3 sm:left-5 sm:p-2">
                    <PartyPopper className="h-4 w-4 text-yellow-100 sm:h-5 sm:w-5" />
                  </div>
                  <div className="absolute -bottom-1.5 right-5 hidden rounded-full bg-white/15 p-1.5 backdrop-blur-md animate-bounce [animation-delay:300ms] sm:block sm:-bottom-2 sm:right-6 sm:p-2">
                    <Star className="h-4 w-4 text-yellow-100 sm:h-5 sm:w-5" />
                  </div>
                  <div className="absolute -right-1.5 top-8 hidden rounded-full bg-white/15 p-1.5 backdrop-blur-md animate-bounce [animation-delay:500ms] sm:block sm:-right-2 sm:top-10 sm:p-2">
                    <Sparkles className="h-4 w-4 text-pink-100 sm:h-5 sm:w-5" />
                  </div>
                  <div className="text-center">
                    <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-white/20 shadow-lg shadow-fuchsia-950/20 backdrop-blur-sm sm:mb-3 sm:h-20 sm:w-20">
                      <Cake className="h-8 w-8 text-white drop-shadow-lg sm:h-10 sm:w-10" />
                    </div>
                    <p className="hidden text-[0.62rem] uppercase tracking-[0.28em] text-white/70 sm:block sm:text-xs sm:tracking-[0.35em]">
                      Celebrate
                    </p>
                    <p className="mt-1 text-base font-black text-white sm:text-2xl">
                      {firstName}
                    </p>
                    <p className="mt-0.5 px-2 text-[0.68rem] leading-4 text-white/80 sm:mt-1 sm:px-0 sm:text-sm">
                      Make a wish and own the day
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hero Section */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)]">
        {/* Welcome Card */}
        <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-[0_18px_45px_rgba(15,23,42,0.55)]">
          {/* Decorative Background */}
          <div className="pointer-events-none absolute inset-0 opacity-30">
            <div className="absolute -left-10 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
            <div className="absolute -right-16 top-8 h-48 w-48 rounded-full bg-purple-400/50 blur-3xl" />
            <div className="absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-blue-300/40 blur-3xl" />
          </div>

          <div className="relative flex flex-col gap-8 p-6 md:p-8 lg:flex-row lg:items-center lg:justify-between">
            {/* Profile Section: Center on mobile, left-aligned from Tablet (md) up */}
            <div className="flex flex-col items-center gap-5 text-center md:flex-row md:text-left">
              <Avatar className="h-20 w-20 flex-shrink-0 border-4 border-white/70 shadow-xl md:h-24 md:w-24">
                <AvatarImage
                  src={employeeData?.user.profilePhotoSignedUrl}
                  alt={employeeData?.user.name}
                />
                <AvatarFallback className="bg-white text-2xl font-bold text-blue-700">
                  {employeeData?.user.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("") || user?.username?.slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 space-y-1">
                <h1 className="text-2xl font-semibold leading-tight md:text-3xl">
                  Welcome back,{" "}
                  <span className="font-bold">
                    {employeeData?.user.name?.split(" ")[0]}!
                  </span>
                </h1>

                {hasFeature("department_mapping") && (
                  <p className="text-sm font-medium text-blue-100/90 md:text-base">
                    {employeeData?.employee.designation} •{" "}
                    {employeeData?.employee.department}
                  </p>
                )}

                <p className="text-xs text-blue-100/70">
                  Employee ID: {employeeData?.employee.employee_id}
                </p>

                <div className="mt-4 flex flex-wrap justify-center gap-2 md:justify-start">
                  <Badge
                    variant="outline"
                    className="border-none bg-white/15 hover:bg-white/40 px-3 py-1 text-[0.7rem] font-medium text-white backdrop-blur-md"
                  >
                    {new Date().toLocaleDateString("en-IN", {
                      weekday: "long",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </Badge>
                  {isOnProbation && (
                    <Badge
                      variant="outline"
                      className="border-none bg-orange-500/90 px-3 py-1 text-[0.7rem] font-medium text-white"
                    >
                      On Probation
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Section: 
        Mobile: Stacked 
        Tablet (md): Side-by-side 
        Desktop (lg): Stacked on the right
    */}
            <div className="flex flex-col gap-4 md:flex-row md:items-stretch lg:flex-col lg:items-end lg:justify-center">
              <div className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-xs font-medium backdrop-blur md:px-6 lg:flex-none lg:rounded-full lg:py-2">
                <Activity className="h-4 w-4 text-emerald-300" />
                <span className="opacity-80">Today:</span>
                <span className="font-semibold">
                  {isLeaveToday
                    ? "On Leave"
                    : isAbsentToday
                    ? "Absent"
                    : attendanceData.todayStatus === "checked-in"
                    ? "Checked In"
                    : attendanceData.todayStatus === "present"
                      ? "Present"
                      : attendanceData.todayStatus === "late"
                      ? "Late"
                      : attendanceData.todayStatus === "half-day"
                        ? "Half Day"
                          : attendanceData.todayStatus === "weekend-ot"
                            ? "OT (Weekend)"
                            : attendanceData.todayStatus === "workday-ot"
                              ? "OT (Working Day)"
                              : attendanceData.todayStatus === "week-off"
                                ? "Week-Off"
                                : attendanceData.todayStatus === "holiday"
                                  ? "Holiday"
                                  : "Not Started"}
                </span>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              </div>

              <div className="flex flex-1 flex-col justify-center rounded-xl bg-black/20 px-6 py-3 backdrop-blur-md md:text-left lg:flex-none lg:text-right">
                <p className="text-[0.65rem] uppercase tracking-widest text-blue-100/60">
                  This Month Hours
                </p>
                <div className="flex items-baseline justify-center gap-2 md:justify-start lg:justify-end">
                  <p className="text-xl font-bold text-white">
                    {attendanceData.thisMonthHours}
                  </p>
                  <p className="text-[0.7rem] text-blue-100/70">
                    (Reg: {Math.round(attendanceData.regularHours)}h • OT:{" "}
                    {Math.round(attendanceData.otHours)}h)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Today's Status Card */}
        <Card className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white/80 shadow-[0_16px_40px_rgba(15,23,42,0.12)] dark:border-slate-700/70 dark:bg-slate-900/80">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-sm font-semibold text-slate-800 dark:text-slate-100">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
                  <Activity className="h-4 w-4" />
                </span>
                <span>Today&apos;s Status</span>
              </div>
              <Badge
                variant="outline"
                className={
                  isLeaveToday || isAbsentToday
                    ? "border-red-500/40 bg-red-50 text-red-700 dark:border-red-500/60 dark:bg-red-900/40 dark:text-red-200"
                    : attendanceData.todayStatus === "present" ||
                  attendanceData.todayStatus === "checked-in" ||
                  attendanceData.todayStatus === "late"
                    ? "border-emerald-500/40 bg-emerald-50 text-emerald-700 dark:border-emerald-500/50 dark:bg-emerald-900/40 dark:text-emerald-200"
                    : attendanceData.todayStatus === "weekend-ot" ||
                          attendanceData.todayStatus === "workday-ot"
                        ? "border-blue-500/40 bg-blue-50 text-blue-700 dark:border-blue-500/60 dark:bg-blue-900/40 dark:text-blue-200"
                        : "border-slate-400/40 bg-slate-50 text-slate-700 dark:border-slate-500/60 dark:bg-slate-900/40 dark:text-slate-200"
                }
              >
                {isLeaveToday
                  ? "On Leave"
                  : isAbsentToday
                  ? "Absent"
                  : attendanceData.todayStatus === "present" ||
                attendanceData.todayStatus === "checked-in" ||
                attendanceData.todayStatus === "late"
                  ? "Working Day"
                  : attendanceData.todayStatus === "half-day"
                    ? "Half Day Leave"
                    : attendanceData.todayStatus === "weekend-ot"
                      ? "Weekend + OT"
                      : attendanceData.todayStatus === "workday-ot"
                        ? "Working Day + OT"
                        : "Weekend / Holiday"}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`grid gap-4 ${
                hasFeature("overtime_management")
                  ? "sm:grid-cols-3"
                  : "sm:grid-cols-2"
              }`}
            >
              <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/70">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Check In
                </p>
                <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {attendanceData.checkInTime || "-"}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/70">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Check Out
                </p>
                <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {attendanceData.checkOutTime || "-"}
                </p>
              </div>

              {hasFeature("overtime_management") && (
                <div className="rounded-xl bg-blue-50 p-3 dark:bg-blue-900/40">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-200">
                    Hours Today (incl. OT)
                  </p>
                  <p className="mt-1 text-lg font-semibold text-blue-900 dark:text-blue-50">
                    {attendanceData.todaysWorkingDuration
                      ? attendanceData.todaysWorkingDuration.toFixed(1) + "h"
                      : "-"}
                  </p>
                </div>
              )}
            </div>

            {isLeaveToday && (
              <div className="rounded-xl border border-red-100 bg-red-50/80 p-4 text-center text-sm text-red-700 dark:border-red-500/50 dark:bg-red-900/40 dark:text-red-200">
                You are on approved leave today. Enjoy your break.
              </div>
            )}

            {isAbsentToday && (
              <div className="rounded-xl border border-red-100 bg-red-50/80 p-4 text-center text-sm text-red-700 dark:border-red-500/50 dark:bg-red-900/40 dark:text-red-200">
                You are marked absent today.
                {hasFeature("regularization_requests") &&
                  " If you were working, apply for regularization on your Attendance page."}
              </div>
            )}

            {(attendanceData.todayStatus === "workday-ot" ||
              attendanceData.todayStatus === "weekend-ot") && (
              <div className="rounded-xl bg-gradient-to-r from-emerald-50 to-blue-50 p-4 text-sm text-slate-800 dark:from-emerald-900/30 dark:to-blue-900/30 dark:text-slate-100">
                <p className="font-medium">
                  {attendanceData.todayStatus === "workday-ot"
                    ? "Working day with overtime logged."
                    : "Weekend overtime logged."}
                </p>
              </div>
            )}

            {attendanceData.todayStatus === "weekend" && (
              <div className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-600 dark:bg-slate-800/70 dark:text-slate-300">
                It&apos;s a weekend or holiday. You can still log overtime if
                you are working.
              </div>
            )}
            {attendanceData.isCheckinOutEnabled &&
              attendanceData.requires_daily_login &&
              (attendanceData.todayStatus === "present" ||
                attendanceData.todayStatus === "late" ||
                attendanceData.todayStatus === "half-day" ||
                attendanceData.isOnApprovedLeave) &&
                device.platform !== "web" && 
              (
                <Button
                  disabled={
                    attendanceData.checkOutTime ||
                    isProcessing ||
                    (!isClockedIn &&
                      (attendanceData.isOnApprovedLeave ||
                        isAfterShiftEnd(todayConfig?.shift?.end_time)))
                  }
                  onClick={() => handlePunch()}
                  className={
                    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 h-10 px-4 py-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-[0_180px_45px_rgba(15,23,42,0.55)]"
                  }
                  //  ${variantClasses} ${className}`
                >
                  {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}

                  {isClockedIn ? "Punch Out" : "Punch In"}
                </Button>
              )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Row – Dynamic columns */}
      <div
        className={`grid gap-6 ${
          hasPerformanceRatings ? "md:grid-cols-3" : "md:grid-cols-2"
        }`}
      >
        {/* Leave Balance */}
        <Card
          className="group relative overflow-hidden rounded-2xl border border-blue-100 bg-gradient-to-b from-white to-blue-50/80 shadow-md transition hover:shadow-xl dark:border-blue-900/40 dark:from-slate-900 dark:to-blue-950/40"
          onClick={() => navigate("/employee/leave")}
        >
          <CardContent className="relative space-y-4 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Leave Balance
                </p>
                {isOnProbation ? (
                  <>
                    <p className="mt-2 text-lg font-semibold text-orange-600 dark:text-orange-400">
                      Probation
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Until{" "}
                      {employeeData?.employee.probation_end_date &&
                        new Date(
                          employeeData.employee.probation_end_date,
                        ).toLocaleDateString()}
                    </p>
                  </>
                ) : (
                  <p className="mt-2 text-3xl font-bold text-blue-700 dark:text-blue-300">
                    {leaveEntitlement
                      ? leaveEntitlement.reduce(
                          (acc, lb) =>
                            acc + (lb.total_allocated - (lb.used || 0)),
                          0,
                        )
                      : "-"}
                  </p>
                )}
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 shadow-sm dark:bg-blue-900/40 dark:text-blue-300">
                <Calendar className="h-5 w-5" />
              </div>
            </div>
            {!isOnProbation && leaveEntitlement && (
              <>
                <Progress
                  value={leaveTotals.percentage}
                  className="h-2 rounded-full bg-blue-100 dark:bg-blue-950/50 [&>div]:bg-blue-600"
                />

                <div className="mt-2 flex items-center justify-between text-[0.7rem] text-slate-500 dark:text-slate-400">
                  <span>Used: {leaveTotals.used.toFixed(1)}</span>
                  <span>Available: {leaveTotals.available.toFixed(1)}</span>
                </div>
              </>
            )}

            <p className="mt-2 flex items-center gap-1 text-[0.7rem] font-medium text-blue-700/80 dark:text-blue-300">
              <Info className="h-3 w-3" />
              Click to view leave details
            </p>
          </CardContent>
        </Card>

        {/* Hours This Month */}
        <Card
          className="relative overflow-hidden rounded-2xl border border-purple-100 bg-gradient-to-b from-white to-purple-50/80 shadow-md transition hover:shadow-xl dark:border-purple-900/40 dark:from-slate-900 dark:to-purple-950/40"
          onClick={() => navigate("/employee/attendance")}
        >
          <CardContent className="relative space-y-4 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Hours This Month
                </p>
                <p className="mt-2 text-3xl font-bold text-purple-700 dark:text-purple-300">
                  {attendanceData.thisMonthHours}
                </p>
                <p className="mt-1 text-[0.7rem] text-slate-500 dark:text-slate-400">
                  Target: 176h
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-600 shadow-sm dark:bg-purple-900/40 dark:text-purple-300">
                <Clock className="h-5 w-5" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[0.7rem] text-slate-500 dark:text-slate-400">
                <span>Regular</span>
                <span>{Math.round(attendanceData.regularHours)}h</span>
              </div>
              <Progress
                value={(attendanceData.regularHours / 176) * 100}
                className="h-2 rounded-full bg-purple-100 dark:bg-purple-950/50 [&>div]:bg-purple-600"
              />
              {attendanceData.otHours > 0 && (
                <>
                  <div className="mt-2 flex items-center justify-between text-[0.7rem] text-blue-700 dark:text-blue-300">
                    <span>Overtime</span>
                    <span>{Math.round(attendanceData.otHours)}h</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-950/50">
                    <div
                      className="h-full rounded-full bg-blue-600 dark:bg-blue-400"
                      style={{
                        width: `${Math.min(
                          (attendanceData.otHours / 20) * 100,
                          100,
                        )}%`,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
            <p className="mt-2 flex items-center gap-1 text-[0.7rem] font-medium text-blue-700/80 dark:text-blue-300">
              <Info className="h-3 w-3" />
              Click to view attendance details
            </p>
          </CardContent>
        </Card>

        {/* Performance – Only if enabled */}
        {hasPerformanceRatings && (
          <Card
            // Keyboard-operable trigger: the card is a div, so it needs an
            // explicit button role, tab stop and Enter/Space handling.
            role="button"
            tabIndex={0}
            aria-haspopup="dialog"
            aria-expanded={showRatingDialog}
            aria-label={
              Number.isFinite(employeeData?.employee?.rating)
                ? `Performance rating ${clampRating(
                    employeeData.employee.rating,
                    ratingScale,
                  ).toFixed(3)} out of ${maxRating}${
                    ratingScaleReady
                      ? ` — ${ratingBand(
                          employeeData.employee.rating,
                          ratingScale,
                        )}`
                      : ""
                  }. Open detailed breakdown`
                : "Performance rating — open detailed breakdown"
            }
            className="relative cursor-pointer overflow-hidden rounded-2xl border border-orange-100 bg-gradient-to-b from-white to-orange-50/80 shadow-md transition hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:border-orange-900/40 dark:from-slate-900 dark:to-orange-950/40"
            onClick={() => setShowRatingDialog(true)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setShowRatingDialog(true);
              }
            }}
          >
            <CardContent className="relative space-y-4 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Performance
                  </p>
                  <p className="mt-2 text-3xl font-bold text-orange-600 dark:text-orange-300">
                    {Number.isFinite(employeeData?.employee?.rating)
                      ? clampRating(
                          employeeData.employee.rating,
                          ratingScale,
                        ).toFixed(3)
                      : "-"}
                    <span className="ml-1 text-base font-medium text-slate-500 dark:text-slate-400">
                      / {maxRating}
                    </span>
                  </p>
                  {/* Progress toward the configured maximum. Replaces the old
                      fixed 5-star row, which implied a 5-point scale even when
                      the admin had configured a different maximum. */}
                  {Number.isFinite(employeeData?.employee?.rating) && (
                    <div className="mt-2 w-32 max-w-full">
                      <RatingMeter
                        rating={employeeData.employee.rating}
                        scale={ratingScale}
                        size="sm"
                        showValue={false}
                        showBand={false}
                      />
                      {ratingScaleReady && (
                        <p className="mt-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                          {ratingBand(employeeData.employee.rating, ratingScale)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600 shadow-sm dark:bg-orange-900/40 dark:text-orange-300">
                  <TrendingUp className="h-5 w-5" aria-hidden="true" />
                </div>
              </div>
              <p className="mt-2 flex items-center gap-1 text-[0.7rem] font-medium text-blue-700/80 dark:text-blue-300">
                <Info className="h-3 w-3" aria-hidden="true" />
                Click to view detailed breakdown
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Notifications + Upcoming Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-teal-600 dark:text-teal-400">
              <UserCheck className="h-5 w-5" />
              Monthly Attendance Snapshot
            </CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Total Working: {total}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {loadingAttendance ? (
              <AttendanceSnapshotSkeleton />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  {items.map((it, i) => {
                    const Icon = it.icon;
                    return (
                      <div
                        key={i}
                        className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div
                          className={`h-8 w-8 flex items-center justify-center text-white rounded-full ${it.color}`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold dark:text-white">
                            {it.value}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {it.label}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <ProgressDisplay
                  label="Present Rate"
                  value={present_rate}
                  color="bg-teal-500"
                />
                <ProgressDisplay
                  label="On-Time Arrival"
                  value={on_time_rate}
                  color="bg-blue-500"
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-purple-200/40 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl shadow-[0_12px_40px_rgba(15,23,42,0.1)] dark:border-purple-900/40">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-purple-700 dark:text-purple-400">
              <Gift className="h-5 w-5" />
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-[420px] overflow-y-auto pr-2 space-y-3">
            {upcomingEvents.length === 0 && (
              <div className="py-10 text-center text-slate-500 dark:text-slate-400">
                <Calendar className="h-10 w-10 mx-auto mb-2 text-slate-400" />
                No upcoming events
              </div>
            )}
            {upcomingEvents.map((event, index) => (
              <div
                key={index}
                className="rounded-xl border border-purple-300/40 bg-gradient-to-r from-purple-50/80 to-blue-50/60 dark:from-purple-900/20 dark:to-blue-900/20 p-3 flex justify-between items-center hover:shadow-md transition"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="text-2xl">{event.icon}</div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {event.title}
                      {event.subtitle && (
                        <span className="ml-1 text-purple-600 dark:text-purple-300">
                          {event.subtitle}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(event.date).toDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <p
                    className={`text-lg font-bold ${
                      event.daysUntil === 0
                        ? "text-red-600 dark:text-red-400"
                        : event.daysUntil <= 7
                          ? "text-orange-600 dark:text-orange-400"
                          : "text-purple-700 dark:text-purple-300"
                    }`}
                  >
                    {event.daysUntil === 0 ? "Today" : event.daysUntil}
                  </p>
                  {event.daysUntil > 0 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {event.daysUntil === 1 ? "day" : "days"}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Leave Entitlement Details */}
      {leaveEntitlement && !isOnProbation && (
        <Card className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
              <Calendar className="h-5 w-5 text-slate-500 dark:text-slate-400" />
              Leave Entitlement
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Grid Logic:
        - Mobile: 1 column (better for small screens)
        - Small/Large Tablet: 2 columns (md:grid-cols-2)
        - Desktop/Large Tablet Landscape: 3 columns (lg:grid-cols-3)
        - Ultra-wide: 4 columns (2xl:grid-cols-4)
    */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {leaveEntitlement.map((lb) => {
                const type = leaveTypes.find((t) => t._id === lb.leave_id);
                const available = lb.total_allocated - (lb.used || 0);
                const percentage =
                  lb.total_allocated > 0
                    ? (available / lb.total_allocated) * 100
                    : 0;
                const isLow = available < 3 && available > 0;

                return (
                  <Card
                    key={lb.leave_id}
                    className={`rounded-xl border bg-white dark:bg-slate-800/40 shadow-sm transition-all hover:shadow-md ${
                      isLow
                        ? "border-amber-300 dark:border-amber-600 ring-1 ring-amber-50 dark:ring-amber-900/20"
                        : "border-slate-200 dark:border-slate-700"
                    }`}
                  >
                    <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full space-y-4">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-[10px] tracking-widest uppercase text-slate-500 dark:text-slate-400 font-bold truncate"
                            title={type?.leave_name || lb.leave_name}
                          >
                            {type?.leave_name || lb.leave_name}
                          </p>
                          <div className="flex items-baseline gap-1 mt-1">
                            <p className="text-2xl sm:text-3xl font-extrabold text-slate-800 dark:text-slate-100">
                              {available}
                            </p>
                            <span className="text-sm font-medium text-slate-500">
                              days
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 whitespace-nowrap">
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              {lb.used || 0}
                            </span>{" "}
                            used •{" "}
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              {lb.total_allocated}
                            </span>{" "}
                            total
                          </p>
                        </div>

                        {/* Icon hidden on very small tablets if space is tight, otherwise visible */}
                        <div
                          className={`hidden xs:flex w-10 h-10 rounded-full flex-shrink-0 items-center justify-center ${
                            isLow
                              ? "bg-amber-100 dark:bg-amber-900/40"
                              : "bg-slate-100 dark:bg-slate-700/50"
                          }`}
                        >
                          <Calendar
                            className={`h-5 w-5 ${
                              isLow
                                ? "text-amber-600 dark:text-amber-300"
                                : "text-slate-600 dark:text-slate-300"
                            }`}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              isLow ? "bg-amber-500" : "bg-indigo-600"
                            }`}
                            style={{ width: `${Math.min(100, percentage)}%` }}
                          />
                        </div>

                        {isLow ? (
                          <p className="text-[10px] text-amber-700 dark:text-amber-400 font-medium flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Low balance
                          </p>
                        ) : (
                          <div className="h-3" /> /* Placeholder to keep height consistent across row */
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Summary Card - Fixed to match the height of sibling items */}
              <div className="rounded-xl p-5 bg-gradient-to-br from-indigo-50 to-purple-100 dark:from-indigo-950/30 dark:to-purple-900/30 border border-indigo-200 dark:border-indigo-800 flex flex-col justify-center">
                <p className="text-xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
                  Total Available
                </p>
                <div className="flex items-baseline gap-1 mt-1">
                  <p className="text-3xl font-black text-indigo-800 dark:text-indigo-200">
                    {leaveEntitlement
                      .reduce(
                        (acc, lb) =>
                          acc + (lb.total_allocated - (lb.used || 0)),
                        0,
                      )
                      .toFixed(1)}
                  </p>
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                    days
                  </span>
                </div>
                <p className="text-[11px] leading-tight text-indigo-600/80 dark:text-indigo-400/80 mt-2">
                  Combined leave remaining for the current cycle.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions – Dynamic grid */}
      <Card className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm transition-shadow hover:shadow-md">
        <CardHeader className="pb-4 sm:pb-5">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
                <Activity className="h-5 w-5 text-indigo-500" />
                Quick Actions
              </CardTitle>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                Frequently used employee options
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Grid Logic:
       - Default: 2 columns (Mobile)
       - md: 3 columns (Tablet Portrait)
       - lg: 4 columns (Tablet Landscape/Small Desktop)
       - xl: 6 columns (Full Desktop)
    */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            <Button
              variant="outline"
              className="group h-24 flex flex-col items-center justify-center gap-2 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-200 shadow-sm"
              onClick={() => handleQuickAction("payslips")}
            >
              <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform" />
              <span className="text-[13px] font-medium text-slate-700 dark:text-slate-200">
                View Payslips
              </span>
            </Button>

            <Button
              variant="outline"
              className="group h-24 flex flex-col items-center justify-center gap-2 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-green-50/50 dark:hover:bg-green-900/10 hover:border-green-200 dark:hover:border-green-800 transition-all duration-200 shadow-sm"
              onClick={() => handleQuickAction("leave")}
            >
              <Calendar className="h-6 w-6 text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform" />
              <span className="text-[13px] font-medium text-slate-700 dark:text-slate-200">
                Apply Leave
              </span>
            </Button>

            {canApplyLoan && device && device?.platform !== "ios" && (
              <Button
                variant="outline"
                className="group h-24 flex flex-col items-center justify-center gap-2 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 hover:border-purple-200 dark:hover:border-purple-800 transition-all duration-200 shadow-sm"
                onClick={() => handleQuickAction("loans")}
              >
                <CreditCard className="h-6 w-6 text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform" />
                <span className="text-[13px] font-medium text-slate-700 dark:text-slate-200">
                  Loan Request
                </span>
              </Button>
            )}

            <Button
              variant="outline"
              className="group h-24 flex flex-col items-center justify-center gap-2 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-orange-50/50 dark:hover:bg-orange-900/10 hover:border-orange-200 dark:hover:border-orange-800 transition-all duration-200 shadow-sm"
              onClick={() => handleQuickAction("profile")}
            >
              <User className="h-6 w-6 text-orange-600 dark:text-orange-400 group-hover:scale-110 transition-transform" />
              <span className="text-[13px] font-medium text-slate-700 dark:text-slate-200">
                Update Profile
              </span>
            </Button>

            <Button
              variant="outline"
              className="group h-24 flex flex-col items-center justify-center gap-2 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 hover:border-indigo-200 dark:hover:border-indigo-800 transition-all duration-200 shadow-sm"
              onClick={() => handleQuickAction("attendance")}
            >
              <Clock className="h-6 w-6 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform" />
              <span className="text-[13px] font-medium text-slate-700 dark:text-slate-200">
                Attendance
              </span>
            </Button>

            {canUploadDocuments && (
              <Button
                variant="outline"
                className="group h-24 flex flex-col items-center justify-center gap-2 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-800/40 hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200 shadow-sm"
                onClick={() => handleQuickAction("documents")}
              >
                <FolderOpen className="h-6 w-6 text-slate-600 dark:text-slate-400 group-hover:scale-110 transition-transform" />
                <span className="text-[13px] font-medium text-slate-700 dark:text-slate-200">
                  Documents
                </span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance breakdown — right-side drawer (full width on mobile).
          Presentational only: it renders the rating already fetched on mount. */}
      <PerformanceRatingDrawer
        open={showRatingDialog}
        onOpenChange={setShowRatingDialog}
        rating={rating}
        otHours={otHours}
        loading={ratingLoading}
        error={ratingError}
        onRetry={fetchEmployeeRating}
      />
      <Dialog
        open={showEarlyCheckoutDialog}
        onOpenChange={(open) => {
          if (isProcessing) return; // 🔒 block close
          setShowEarlyCheckoutDialog(open);
        }}
      >
        <DialogContent className="max-w-md rounded-2xl border border-border bg-background shadow-xl p-0 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 border-b border-border bg-amber-50 dark:bg-amber-900/20">
            <DialogTitle className="flex items-center gap-2 text-base font-semibold text-amber-800 dark:text-amber-300">
              <AlertTriangle className="h-5 w-5" />
              Early Checkout Confirmation
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-amber-700 dark:text-amber-400">
              {earlyCheckoutData?.message ||
                "You are attempting to check out before your shift ends."}
            </DialogDescription>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-4">
            <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Shift ends at</span>
                <span className="font-semibold">
                  {earlyCheckoutData?.shiftEndTime}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Current time</span>
                <span className="font-semibold">
                  {earlyCheckoutData?.currentTime}
                </span>
              </div>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              If you proceed, this checkout will be recorded as an
              <span className="font-medium text-foreground">
                {" "}
                early checkout{" "}
              </span>
              and may affect your attendance or payroll calculations.
            </p>

            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
              Early checkout will be marked as an{" "}
              <span className="font-medium">unpaid half day</span>. You can
              regularize it later if needed.
            </p>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-muted/30 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowEarlyCheckoutDialog(false)}
            >
              Go Back
            </Button>

            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              onClick={async () => {
                setShowEarlyCheckoutDialog(false);
                await handleEarlyCheckout();
              }}
            >
              {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirm Checkout
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EmployeeDashboardSkeleton = () => {
  return (
    <div className="space-y-8">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.3fr)]">
        <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-blue-600/90 via-indigo-600/90 to-purple-600/90 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.35)] md:p-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col items-center gap-5 text-center md:flex-row md:text-left">
              <Skeleton className="h-20 w-20 rounded-full bg-white/25 md:h-24 md:w-24" />
              <div className="space-y-3">
                <Skeleton className="h-8 w-56 bg-white/25" />
                <Skeleton className="h-4 w-44 bg-white/20" />
                <Skeleton className="h-3 w-28 bg-white/15" />
                <div className="flex flex-wrap justify-center gap-2 md:justify-start">
                  <Skeleton className="h-7 w-36 rounded-full bg-white/20" />
                  <Skeleton className="h-7 w-24 rounded-full bg-white/15" />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Skeleton className="h-12 w-56 rounded-xl bg-white/20" />
              <Skeleton className="h-16 w-56 rounded-xl bg-black/20" />
            </div>
          </div>
        </div>

        <Card className="rounded-2xl border border-slate-200/70 bg-white/80 dark:border-slate-700/70 dark:bg-slate-900/80">
          <CardHeader className="space-y-3">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-4 w-24" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-slate-50 p-3 dark:bg-slate-800/70"
                >
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="mt-3 h-7 w-24" />
                </div>
              ))}
            </div>
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="h-10 w-full rounded-xl" />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <Card key={index}>
            <CardContent className="space-y-4 p-6">
              <Skeleton className="h-10 w-10 rounded-lg" />
              <Skeleton className="h-7 w-20" />
              <Skeleton className="h-4 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {[...Array(2)].map((_, index) => (
          <Card key={index}>
            <CardHeader className="space-y-3">
              <Skeleton className="h-6 w-44" />
              <Skeleton className="h-4 w-32" />
            </CardHeader>
            <CardContent className="space-y-4">
              {[...Array(4)].map((__, innerIndex) => (
                <div key={innerIndex} className="rounded-lg border p-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="mt-3 h-3 w-full" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const AttendanceSnapshotSkeleton = () => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="flex items-center space-x-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-700"
          >
            <Skeleton className="h-8 w-8 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-2 w-full rounded-full" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-2 w-full rounded-full" />
      </div>
    </div>
  );
};

const ProgressDisplay = ({ label, value, color = "bg-primary" }) => {
  const safeValue = Number.isFinite(value)
    ? Math.max(0, Math.min(100, value))
    : 0;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-sm font-semibold text-foreground">{safeValue}%</p>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all`}
          style={{ width: `${safeValue}%` }}
        />
      </div>
    </div>
  );
};

export default EmployeeDashboard;
